import { useQuery } from '@tanstack/react-query'

import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type {
  VipExperience,
  VipFtdClaimStatus,
  VipTierConfig,
  VipTierKey,
  VipUpgradeRequirement,
} from '../../types/vip-center'
import { api } from '../../utils/api-client'
import { queryKeys } from './keys'

// Raw vip_status payload: { status, info: { current_level, next_level, exp,
// all_levels, … } }. The full tier list now lives in `all_levels` — vip_lists
// is no longer called.

// current_level / next_level — only the id (join key) and the next-tier
// thresholds are read here.
interface RawStatusLevel {
  // Tier id — the join key, matches an all_levels row's `id`.
  id?: number | string
  upgrade_recharge_exp?: number
  upgrade_betting_exp?: number
}

// An all_levels row. Carries per-tier upgrade thresholds directly
// (upgrade_*_exp); current_level / next_level are kept as a fallback for rows
// that omit them.
interface RawVipLevel {
  id: number
  // Player-facing display name, e.g. "Level 1".
  name?: string
  sort?: number
  // Remote VIP badge URLs per theme (empty string on the high tiers).
  icon_light?: string
  icon_dark?: string
  // Tier-up bonus amount for this tier.
  bonus_amount?: number
  birthday_bonus?: number
  // Per-tier upgrade thresholds — the EXP needed to reach this tier.
  upgrade_recharge_exp?: number
  upgrade_betting_exp?: number
  // How the two thresholds combine — drives the i18n upgrade-condition copy.
  upgrade_requirement?: string
}

// Legacy (pre-flatten) FTD shape: the claim-window deadline and status used to
// be nested under `ftd_expires_at`. Tolerated so a rollout window where the
// backend still nests them maps correctly instead of rendering an open bonus
// as expired.
interface RawLegacyFtdExpires {
  expires_at?: string | null
  claim_status?: VipFtdClaimStatus | null
}

// info — the actual status body.
interface RawStatusBody {
  // true right after an upgrade — drives the FE-04 popup.
  upgraded?: boolean
  current_level?: RawStatusLevel
  next_level?: RawStatusLevel | null
  exp?: { recharge_exp?: number; betting_exp?: number }
  // Full tier list (replaces the former vip_lists call).
  all_levels?: RawVipLevel[]
  // First-deposit (FTD) bonus shown on the base tier (Nuevo). `ftd_claimed` is
  // 1 once the player has redeemed it; `ftd_expires_at` is the claim-window
  // deadline as a plain timestamp string (null before the window opens — i.e.
  // before the first deposit); `ftd_claim_status` is the authoritative claim
  // state. Backend uses "deposit" — the FE keeps the "recharge" vocabulary it
  // uses everywhere else (rechargeExp, recharge_exp, etc.); the mapper
  // translates. `ftd_expires_at` also accepts the legacy nested
  // `{ expires_at, claim_status }` shape so a rollout window where the backend
  // hasn't flattened it yet still maps correctly (see the mapper).
  first_deposit_bonus?: number
  ftd_claimed?: number
  ftd_expires_at?: string | RawLegacyFtdExpires | null
  ftd_claim_status?: VipFtdClaimStatus | null
  // 1 once the player has completed KYC — gates the FTD claim.
  is_kyc_verified?: number
  // 1 while the player is eligible for the base-tier FTD bonus. Flips to 0 once
  // the bonus is claimed (or the player was never eligible / it was withdrawn) —
  // an ineligible player can't act on the bonus (the recharge CTA is disabled).
  ftd_eligible?: number
  // 1 once the player has made their first deposit — the FTD claim window only
  // opens after a deposit, so before it the CTA prompts a recharge instead.
  has_deposited?: number
  // VIP customer-support links: Telegram/support URL + WhatsApp.
  support_url?: string | null
  whatsapp?: string | null
}

export interface VipStatus {
  // The full tier ladder, ordered by `order` ascending (from all_levels).
  tiers: VipTierConfig[]
  currentTierKey: VipTierKey | null
  rechargeExp: VipExperience
  wagerExp: VipExperience
  // Progress toward the next tier (0–100).
  rechargePct: number
  wagerPct: number
  showUpgrade: boolean
  // First-deposit (FTD) bonus (base tier): amount, whether it's been claimed,
  // the claim-window deadline (null until the window opens), and the backend's
  // authoritative claim status (unclaimed | claimed | expired; null if absent).
  firstRechargeBonus: number
  ftdClaimed: boolean
  ftdExpiresAt: string | null
  ftdClaimStatus: VipFtdClaimStatus | null
  // Whether the player has completed KYC — required to claim the FTD bonus.
  isKycVerified: boolean
  // Whether the player is currently eligible for the FTD bonus (false once
  // claimed / never eligible). Gates the recharge CTA — an ineligible player
  // sees it disabled.
  ftdEligible: boolean
  // Whether the player has made their first deposit — the FTD claim window only
  // opens post-deposit.
  hasDeposited: boolean
  // VIP customer-support links (support_url = Telegram/support, whatsapp).
  supportUrl: string | null
  whatsapp: string | null
}

// Tier keys are the stringified `id` — the stable identifier present on every
// tier (the high tiers omit `name`). Used as the join key for current/next.
const toTierKey = (id?: number | string): VipTierKey | null => {
  const key = id != null ? String(id).trim() : ''
  return key || null
}

// Backend `upgrade_requirement` is a free string; narrow it to the union the
// upgrade-condition i18n branches in tier-rewards.tsx switch on. Unknown values
// fall through to undefined, which renders the generic upgrade-condition copy.
const ALLOWED_REQUIREMENTS: VipUpgradeRequirement[] = [
  'simultaneously',
  'recharge_only',
  'betting_only',
  'any',
]
const toUpgradeRequirement = (
  value?: string,
): VipUpgradeRequirement | undefined =>
  ALLOWED_REQUIREMENTS.includes(value as VipUpgradeRequirement)
    ? (value as VipUpgradeRequirement)
    : undefined

// Maps an all_levels row to the VipTierConfig the selector + rewards panel
// consume. Per-tier upgrade thresholds come straight from the row; a 0/missing
// value is later backfilled from current_level / next_level.
const mapLevel = (level: RawVipLevel): VipTierConfig | null => {
  const key = toTierKey(level.id)
  if (!key) return null

  return {
    key,
    name: level.name || key,
    order: level.sort ?? level.id ?? 0,
    iconLight: level.icon_light || undefined,
    iconDark: level.icon_dark || undefined,
    rechargeThreshold: level.upgrade_recharge_exp ?? 0,
    wagerThreshold: level.upgrade_betting_exp ?? 0,
    birthdayBonusAmount: level.birthday_bonus ?? 0,
    // Tier-up bonus amount, sourced directly from this all_levels row.
    promoAmount: level.bonus_amount ?? 0,
    upgradeRequirement: toUpgradeRequirement(level.upgrade_requirement),
  }
}

// Progress of one experience track toward the next-tier threshold, clamped to
// 0–100. A 0 threshold (already at max tier) reports 100%.
const experiencePercent = (exp: VipExperience): number => {
  if (exp.threshold <= 0) return 100
  return Math.min((exp.current / exp.threshold) * 100, 100)
}

// Fetches + maps the player's VIP status (vip_status): the full tier ladder
// (all_levels) + current level + live recharge/betting EXP. Exported so the
// sidebar preloader can warm the same query cache the hook reads.
export const fetchVipStatus = async (
  signal?: AbortSignal,
): Promise<VipStatus> => {
  const res = await api.post<ApiResponse<RawStatusBody>>(
    '/vip.php',
    { type: 'vip_status' },
    { signal },
  )
  // Throw on a failed status so React Query enters its error state. Returning
  // a sentinel here would read as a successful "Nuevo / zero progress" status
  // and silently mislead the player on an API failure.
  if (res.data.status !== 1) throw new Error('vip_status failed')
  // Defensive: if the backend returns the bare tier-config array instead of
  // the per-player object, fall back to an empty body so the page degrades
  // to "no current tier / zero progress" instead of crashing.
  const info = res.data.info
  const body: RawStatusBody = Array.isArray(info) ? {} : (info ?? {})
  const current = body.current_level
  const next = body.next_level
  const showUpgrade = Boolean(body.upgraded)

  // all_levels carries per-tier thresholds directly (mapped in mapLevel).
  // current_level / next_level expose the same `upgrade_*_exp` and serve as a
  // fallback for any all_levels row that omits them.
  const thresholdById = new Map<
    VipTierKey,
    { recharge: number; wager: number }
  >()
  for (const lvl of [current, next]) {
    const key = toTierKey(lvl?.id)
    if (key) {
      thresholdById.set(key, {
        recharge: lvl?.upgrade_recharge_exp ?? 0,
        wager: lvl?.upgrade_betting_exp ?? 0,
      })
    }
  }

  const tiers = (body.all_levels ?? [])
    .map(mapLevel)
    .filter((tier): tier is VipTierConfig => tier !== null)
    .map(tier => {
      const th = thresholdById.get(tier.key)
      return {
        ...tier,
        rechargeThreshold: tier.rechargeThreshold || th?.recharge || 0,
        wagerThreshold: tier.wagerThreshold || th?.wager || 0,
      }
    })
    .sort((a, b) => a.order - b.order)

  const rechargeExp: VipExperience = {
    current: body.exp?.recharge_exp ?? 0,
    threshold: next?.upgrade_recharge_exp ?? 0,
  }
  const wagerExp: VipExperience = {
    current: body.exp?.betting_exp ?? 0,
    threshold: next?.upgrade_betting_exp ?? 0,
  }
  const rechargePct = experiencePercent(rechargeExp)
  const wagerPct = experiencePercent(wagerExp)

  // FTD deadline + claim status, tolerating both the flat shape (string +
  // sibling ftd_claim_status) and the legacy nested `ftd_expires_at` object so
  // a rollout window with either backend version maps correctly.
  const rawFtdExpires = body.ftd_expires_at
  const legacyFtd = typeof rawFtdExpires === 'object' ? rawFtdExpires : null
  const ftdExpiresAt =
    typeof rawFtdExpires === 'string'
      ? rawFtdExpires
      : (legacyFtd?.expires_at ?? null)
  const ftdClaimStatus =
    body.ftd_claim_status ?? legacyFtd?.claim_status ?? null

  return {
    tiers,
    // vip_status levels mirror all_levels rows: join on tier id.
    currentTierKey: toTierKey(current?.id),
    rechargeExp,
    wagerExp,
    rechargePct,
    wagerPct,
    showUpgrade,
    firstRechargeBonus: body.first_deposit_bonus ?? 0,
    ftdClaimed: body.ftd_claimed === 1,
    ftdExpiresAt,
    ftdClaimStatus,
    isKycVerified: body.is_kyc_verified === 1,
    ftdEligible: body.ftd_eligible === 1,
    hasDeposited: body.has_deposited === 1,
    supportUrl: body.support_url ?? null,
    whatsapp: body.whatsapp ?? null,
  }
}

const useVipStatus = (
  // `refetchOnMount: 'always'` lets a consumer (the VIP center page) force a
  // fresh vip_status on every mount instead of reusing the sidebar-preloaded
  // cache. Left undefined elsewhere so those consumers keep the default.
  { refetchOnMount }: { refetchOnMount?: boolean | 'always' } = {},
) => {
  const username = useBoundStore(state => state.user.username)

  return useQuery<VipStatus>({
    queryKey: queryKeys.vipCenter.progress(username),
    queryFn: ({ signal }) => fetchVipStatus(signal),
    enabled: !!username,
    refetchOnMount,
  })
}

export default useVipStatus
